import { useState } from 'react';
import { Building2, Check, ChevronDown, Briefcase, User, Users, Plus, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useCompanyContext } from '../../contexts/CompanyContext';
import { useAuth } from '../../hooks/useAuth';
import { useCompanies } from '../../hooks/useCompanies';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { cn } from '../ui/utils';

/**
 * CompanySwitcher - Multi-select company dropdown
 * 
 * Features:
 * - Multi-select companies with checkboxes
 * - Shows count of selected companies
 * - Quick actions: Select All, Clear Selection
 * - Animated transitions
 * - Mobile-friendly touch targets
 * - Keyboard accessible
 * 
 * @example
 * ```tsx
 * <CompanySwitcher />
 * ```
 */

interface CompanySwitcherProps {
  className?: string;
}

export function CompanySwitcher({ className }: CompanySwitcherProps) {
  const { user } = useAuth();
  const { createCompany } = useCompanies(user?.id);
  const {
    allCompanies,
    selectedCompanies,
    toggleCompany,
    selectAll,
    selectNone,
    isSelected,
    selectCompany,
  } = useCompanyContext();

  const [open, setOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    entity_type: 'business' as 'business' | 'personal' | 'individual',
    description: '',
    industry: '',
    tax_id: '',
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('O nome é obrigatório');
      return;
    }

    setCreating(true);
    try {
      const newCompany = await createCompany(formData);
      toast.success('Empresa criada com sucesso!');
      
      // Auto-select the newly created company
      if (newCompany?.id) {
        // Wait a bit for the context to update
        setTimeout(() => {
          selectCompany(newCompany.id);
        }, 100);
      }
      
      setIsDialogOpen(false);
      setOpen(false);
      setFormData({
        name: '',
        entity_type: 'business',
        description: '',
        industry: '',
        tax_id: '',
      });
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar empresa');
    } finally {
      setCreating(false);
    }
  };

  const getEntityIcon = (type: 'personal' | 'business' | 'individual') => {
    switch (type) {
      case 'business':
        return <Building2 className="h-4 w-4" />;
      case 'personal':
        return <User className="h-4 w-4" />;
      case 'individual':
        return <Briefcase className="h-4 w-4" />;
      default:
        return <Building2 className="h-4 w-4" />;
    }
  };

  const getEntityLabel = (type: 'personal' | 'business' | 'individual') => {
    switch (type) {
      case 'business':
        return 'Empresa';
      case 'personal':
        return 'Pessoal';
      case 'individual':
        return 'Individual';
      default:
        return '';
    }
  };

  const getSwitcherLabel = () => {
    if (selectedCompanies.length === 0) {
      return 'Selecione uma empresa';
    }
    
    if (selectedCompanies.length === 1) {
      return selectedCompanies[0].name;
    }
    
    if (selectedCompanies.length === allCompanies.length) {
      return 'Todas as empresas';
    }
    
    return `${selectedCompanies.length} empresas`;
  };

  const allSelected = selectedCompanies.length === allCompanies.length;

  return (
    <>
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'w-full justify-between px-2 h-auto py-2',
            'hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary',
            className
          )}
          aria-label="Selecionar empresas"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10 text-primary flex-shrink-0">
              {selectedCompanies.length === 1 ? (
                getEntityIcon(selectedCompanies[0].entity_type)
              ) : (
                <Users className="h-4 w-4" />
              )}
            </div>
            <div className="flex flex-col items-start min-w-0 flex-1">
              <span className="truncate">
                {getSwitcherLabel()}
              </span>
              {selectedCompanies.length === 1 && (
                <small className="truncate text-muted-foreground">
                  {getEntityLabel(selectedCompanies[0].entity_type)}
                </small>
              )}
              {selectedCompanies.length > 1 && (
                <small className="truncate text-muted-foreground">
                  Visão consolidada • Carregamento pode demorar
                </small>
              )}
            </div>
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform flex-shrink-0',
              open && 'rotate-180'
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent
        align="start"
        className="w-[280px]"
        sideOffset={8}
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">
          Empresas
        </DropdownMenuLabel>
        
        <div className="max-h-[300px] overflow-y-auto">
          {allCompanies.map((company) => {
            const selected = isSelected(company.id);
            
            return (
              <DropdownMenuItem
                key={company.id}
                className={cn(
                  'flex items-center gap-3 px-2 py-2.5 cursor-pointer',
                  'focus:bg-accent',
                  selected && 'bg-accent/50'
                )}
                onSelect={(e) => {
                  e.preventDefault();
                  toggleCompany(company.id);
                }}
              >
                <div
                  className={cn(
                    'flex items-center justify-center w-5 h-5 rounded border-2 transition-colors flex-shrink-0',
                    selected
                      ? 'bg-primary border-primary'
                      : 'border-input bg-background'
                  )}
                  role="checkbox"
                  aria-checked={selected}
                >
                  <AnimatePresence mode="wait">
                    {selected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Check className="h-3 w-3 text-primary-foreground" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted flex-shrink-0">
                  {getEntityIcon(company.entity_type)}
                </div>

                <div className="flex flex-col min-w-0 flex-1">
                  <span className="truncate text-sm font-medium">
                    {company.name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {getEntityLabel(company.entity_type)}
                  </span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </div>

        <DropdownMenuSeparator />

        <div className="p-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full h-9 text-sm justify-start"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDialogOpen(true);
              setOpen(false);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Criar Nova Empresa
          </Button>
        </div>

        {allCompanies.length > 1 && (
          <>
            <DropdownMenuSeparator />
            
            <div className="grid grid-cols-2 gap-2 p-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (allSelected) {
                    selectNone();
                  } else {
                    selectAll();
                  }
                }}
              >
                {allSelected ? 'Limpar' : 'Todas'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                }}
              >
                Fechar
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent>
        <form onSubmit={handleCreate}>
          <DialogHeader>
            <DialogTitle>Criar Nova Empresa</DialogTitle>
            <DialogDescription>
              Preencha os dados da empresa que deseja gerenciar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome da empresa"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="entity_type">Tipo de Entidade</Label>
              <Select
                value={formData.entity_type}
                onValueChange={(value: any) =>
                  setFormData({ ...formData, entity_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Empresa</SelectItem>
                  <SelectItem value="personal">Pessoal</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Breve descrição"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">Setor</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                placeholder="Ex: Tecnologia, Comércio..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_id">CNPJ/CPF</Label>
              <Input
                id="tax_id"
                value={formData.tax_id}
                onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                placeholder="00.000.000/0000-00"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={creating}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={creating}>
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Criar Empresa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </>
  );
}
